import type { Asset } from "@studio/shared";
import { useQueryClient } from "@tanstack/react-query";
import { RotateCcw } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button, Sheet } from "./ui";

/** A rectangle in fractions of the picture (0..1), so it survives any on-screen size. */
type Box = { x: number; y: number; w: number; h: number };
type Handle = "move" | "nw" | "ne" | "sw" | "se" | "new";

const START: Box = { x: 0.25, y: 0.25, w: 0.5, h: 0.5 };
/** The smallest close-up worth sending, in picture pixels. */
const MIN_SIDE = 64;

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

/**
 * Cuts a close-up out of a reference picture at the picture's own resolution: a label, a logo, a face. The crop
 * is added as another reference next to the original, so the model gets many more tokens on that detail than the
 * whole picture scaled down to the video can give it.
 */
export function ImageCropEditor({
  asset,
  open,
  onOpenChange,
  onSaved,
}: {
  asset: Asset | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: (crop: Asset) => void;
}) {
  const image = useRef<HTMLImageElement>(null);
  const drag = useRef<{ handle: Handle; start: { x: number; y: number }; box: Box } | null>(null);
  const [box, setBox] = useState<Box>(START);
  const [natural, setNatural] = useState<{ w: number; h: number } | null>(null);
  const [saving, setSaving] = useState(false);
  const queryClient = useQueryClient();

  // a new picture (or reopening) starts from the default frame and waits for the picture to load
  const assetId = asset?.id;
  useEffect(() => {
    if (!open || !assetId) return;
    setBox(START);
    setNatural(null);
  }, [open, assetId]);

  const minW = natural ? MIN_SIDE / natural.w : 0.05;
  const minH = natural ? MIN_SIDE / natural.h : 0.05;

  const pointFrom = (event: React.PointerEvent) => {
    const rect = image.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    return {
      x: clamp((event.clientX - rect.left) / rect.width, 0, 1),
      y: clamp((event.clientY - rect.top) / rect.height, 0, 1),
    };
  };

  const begin = (handle: Handle) => (event: React.PointerEvent) => {
    if (!natural) return;
    event.stopPropagation();
    event.preventDefault();
    const start = pointFrom(event);
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
    drag.current = { handle, start, box: handle === "new" ? { x: start.x, y: start.y, w: 0, h: 0 } : box };
    if (handle === "new") setBox({ x: start.x, y: start.y, w: minW, h: minH });
  };

  const move = (event: React.PointerEvent) => {
    const d = drag.current;
    if (!d) return;
    const p = pointFrom(event);
    const dx = p.x - d.start.x;
    const dy = p.y - d.start.y;
    const b = d.box;
    if (d.handle === "move") {
      setBox({ ...b, x: clamp(b.x + dx, 0, 1 - b.w), y: clamp(b.y + dy, 0, 1 - b.h) });
      return;
    }
    // the corner opposite the one being dragged stays put
    let x0 = b.x;
    let y0 = b.y;
    let x1 = b.x + b.w;
    let y1 = b.y + b.h;
    if (d.handle === "new") {
      x0 = d.start.x;
      y0 = d.start.y;
      x1 = p.x;
      y1 = p.y;
    } else {
      if (d.handle === "nw" || d.handle === "sw") x0 = clamp(b.x + dx, 0, x1 - minW);
      if (d.handle === "ne" || d.handle === "se") x1 = clamp(b.x + b.w + dx, x0 + minW, 1);
      if (d.handle === "nw" || d.handle === "ne") y0 = clamp(b.y + dy, 0, y1 - minH);
      if (d.handle === "sw" || d.handle === "se") y1 = clamp(b.y + b.h + dy, y0 + minH, 1);
    }
    const left = Math.min(x0, x1);
    const top = Math.min(y0, y1);
    setBox({
      x: left,
      y: top,
      w: clamp(Math.abs(x1 - x0), minW, 1 - left),
      h: clamp(Math.abs(y1 - y0), minH, 1 - top),
    });
  };

  const end = (event: React.PointerEvent) => {
    const el = event.currentTarget as HTMLElement;
    if (el.hasPointerCapture(event.pointerId)) el.releasePointerCapture(event.pointerId);
    drag.current = null;
  };

  const pixels = natural
    ? {
        x: Math.round(box.x * natural.w),
        y: Math.round(box.y * natural.h),
        w: Math.max(1, Math.round(box.w * natural.w)),
        h: Math.max(1, Math.round(box.h * natural.h)),
      }
    : null;

  const save = async () => {
    const img = image.current;
    if (!img || !asset || !pixels || saving) return;
    setSaving(true);
    try {
      const canvas = document.createElement("canvas");
      canvas.width = pixels.w;
      canvas.height = pixels.h;
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("The close-up could not be drawn.");
      ctx.drawImage(img, pixels.x, pixels.y, pixels.w, pixels.h, 0, 0, pixels.w, pixels.h);
      const blob = await new Promise<Blob>((resolve, reject) =>
        canvas.toBlob(
          (value) => (value ? resolve(value) : reject(new Error("The close-up could not be encoded."))),
          "image/png",
        ),
      );
      const base = (asset.name ?? "reference").replace(/\.[^.]+$/, "");
      const crop = await api.upload(new File([blob], `${base}-closeup.png`, { type: "image/png" }));
      await queryClient.invalidateQueries({ queryKey: ["assets"] });
      onSaved(crop);
      onOpenChange(false);
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const handles: { id: Handle; className: string }[] = [
    { id: "nw", className: "-left-2 -top-2 cursor-nwse-resize" },
    { id: "ne", className: "-right-2 -top-2 cursor-nesw-resize" },
    { id: "sw", className: "-bottom-2 -left-2 cursor-nesw-resize" },
    { id: "se", className: "-bottom-2 -right-2 cursor-nwse-resize" },
  ];

  return (
    <Sheet
      open={open}
      onOpenChange={onOpenChange}
      title="Crop a close-up"
      description="Frame the detail that must stay exact: a label, small print, a logo, a face. It is added as another reference at the picture's full resolution."
      wide
    >
      <div className="flex min-h-[300px] items-center justify-center overflow-hidden rounded-[var(--radius-media)] bg-black/60">
        {asset ? (
          <div
            className="relative touch-none select-none"
            onPointerDown={begin("new")}
            onPointerMove={move}
            onPointerUp={end}
            onPointerCancel={end}
          >
            <img
              ref={image}
              src={asset.url}
              crossOrigin="anonymous"
              alt=""
              draggable={false}
              onLoad={(e) =>
                setNatural({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })
              }
              onError={() => toast.error("The picture could not be opened for cropping.")}
              className="block max-h-[62dvh] max-w-full object-contain"
            />
            {natural ? (
              <div
                role="slider"
                aria-label="Close-up frame: drag to move, drag a corner to resize"
                aria-valuenow={Math.round(box.w * 100)}
                tabIndex={0}
                onPointerDown={begin("move")}
                className="absolute cursor-move border-2 border-[#f7cf4f] shadow-[0_0_0_9999px_rgba(0,0,0,0.55)]"
                style={{
                  left: `${box.x * 100}%`,
                  top: `${box.y * 100}%`,
                  width: `${box.w * 100}%`,
                  height: `${box.h * 100}%`,
                }}
              >
                {handles.map((h) => (
                  <span
                    key={h.id}
                    onPointerDown={begin(h.id)}
                    className={`absolute size-4 rounded-full border-2 border-black bg-[#f7cf4f] ${h.className}`}
                  />
                ))}
              </div>
            ) : (
              <span className="absolute inset-0 flex items-center justify-center text-sm text-mute">
                Opening picture…
              </span>
            )}
          </div>
        ) : null}
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <span className="tabular mr-auto text-[13px] text-mute">
          {pixels ? `${pixels.w} × ${pixels.h} px` : ""}
        </span>
        <Button size="sm" variant="quiet" onClick={() => setBox(START)} disabled={saving}>
          <RotateCcw className="size-4" aria-hidden />
          Reset
        </Button>
        <Button size="sm" variant="primary" onClick={save} disabled={!pixels || saving}>
          {saving ? "Saving…" : "Add close-up"}
        </Button>
      </div>
    </Sheet>
  );
}
